import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Boxes, Loader2, KeyRound } from "lucide-react";

// === AYUDA DE DESARROLLO — credenciales de prueba (REMOVER al finalizar el proyecto) ===
const DEV_CREDENTIALS = [
  { label: "Super Admin", email: "admin@costos.com", password: "Admin123!" },
  { label: "Editor", email: "editor@costos.com", password: "Editor123!" },
  { label: "Consulta", email: "consulta@costos.com", password: "Consulta123!" },
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1527576539890-dfa815648363?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Edificio corporativo"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/85" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-emerald-500 flex items-center justify-center">
              <Boxes className="h-6 w-6 text-slate-900" strokeWidth={2.2} />
            </div>
            <span className="font-heading text-lg font-extrabold tracking-tight">ZAE Logística</span>
          </div>
          <div>
            <h2 className="font-heading text-4xl font-extrabold tracking-tight leading-tight">
              Control total de costos,<br />precios y aduanas.
            </h2>
            <p className="mt-4 text-slate-300 max-w-md">
              Consolida precios diarios, pagos aduanales, facturas de inspectores y presupuesto en un
              estado de resultados claro.
            </p>
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-[0.2em]">Plataforma de gestión financiera</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center">
              <Boxes className="h-5 w-5 text-emerald-400" />
            </div>
            <span className="font-heading font-extrabold tracking-tight text-slate-900">ZAE Logística</span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Iniciar sesión</h1>
          <p className="text-slate-500 mt-1 text-sm">Ingresa tus credenciales para continuar.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">Correo</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="correo@empresa.com"
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">Contraseña</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">
                {error}
              </p>
            )}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Ingresar
            </button>
          </form>

          {/* === AYUDA DE DESARROLLO — REMOVER al finalizar el proyecto === */}
          <div className="mt-8 rounded-md border border-dashed border-amber-300 bg-amber-50/60 p-3" data-testid="dev-credentials">
            <div className="flex items-center gap-1.5 text-amber-700 mb-2">
              <KeyRound className="h-3.5 w-3.5" />
              <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">Credenciales de prueba (desarrollo)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {DEV_CREDENTIALS.map((c) => (
                <button
                  key={c.email}
                  type="button"
                  data-testid={`dev-cred-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => { setEmail(c.email); setPassword(c.password); setError(""); }}
                  className="text-left rounded-md border border-amber-200 bg-white px-2.5 py-1.5 hover:border-amber-400 hover:bg-amber-50 transition-colors"
                  title={`${c.email} / ${c.password}`}
                >
                  <span className="block text-xs font-semibold text-slate-800">{c.label}</span>
                  <span className="block text-[10px] text-slate-500">{c.email}</span>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-amber-700/80 mt-2">Haz clic para autocompletar. Se removerá al finalizar el proyecto.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
